import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import GameCard from "../components/games/GameCard";
import Button from "../components/ui/Button";
import GlassPanel from "../components/ui/GlassPanel";
import Modal from "../components/ui/Modal";
import { AiBadge, OfficialBadge, SourceChip } from "../components/ui/SourceBadge";
import Tooltip from "../components/ui/Tooltip";
import ShareIcon from "../components/ui/ShareIcon";
import { getPlatformFamilies } from "../utils/platforms";
import { getMatchInfo } from "../utils/recommendationReason";
import {
  getGameById,
  getGameProsAndCons,
  getSmartRecommendations,
  trackGameInteraction,
} from "../api/games";
import { useFavorites } from "../context/FavoritesContext";
import { useAuth } from "../AuthContext";
import { recordInteraction, INTERACTION_TYPES } from "../api/interactions";

// Where a game's data was imported from — used to attribute scores/descriptions.
const dataSourceOf = (game) =>
  game?.rawg_id ? "RAWG" : game?.igdb_id ? "IGDB" : null;

// Helper function to properly capitalize text
const capitalizeText = (text) => {
  if (!text) return text;
  return text
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

// Helper function to safely parse JSON fields that might be strings or already parsed
const safeJsonParse = (field) => {
  if (!field) return [];
  if (Array.isArray(field)) return field;
  if (typeof field === "string") {
    try {
      const parsed = JSON.parse(field);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
};

// A screenshot may be an object ({ url|image|image_url|src }) or a bare URL
// string. Also normalise protocol-relative URLs (e.g. IGDB "//images.igdb.com/…")
// so the browser can actually load them.
const screenshotUrl = (s) => {
  let url = typeof s === "string" ? s : s?.url || s?.image || s?.image_url || s?.src || "";
  if (url && url.startsWith("//")) url = `https:${url}`;
  return url;
};

// A pros/cons entry (from the `pros_cons` table) may be a plain string or an
// object ({ text | point | value | pro | con | description }). Normalise to a
// string so the render (which calls .replace) never crashes on an object.
const toPointText = (item) => {
  if (item == null) return "";
  if (typeof item === "string") return item;
  if (typeof item === "object") {
    return (
      item.text || item.point || item.value || item.pro || item.con || item.description || ""
    );
  }
  return String(item);
};
const toPointList = (arr) =>
  Array.isArray(arr) ? arr.map(toPointText).filter(Boolean) : [];

const chipClass =
  "text-white border border-brand-gold/70 rounded-full text-sm font-semibold px-3 py-1";

// Circular icon action button used in the hero action bar.
const iconActionClass =
  "h-11 w-11 rounded-full flex items-center justify-center text-xl border border-white/12 bg-white/[0.05] text-white/85 transition-all duration-200 hover:border-brand-gold/50 hover:text-brand-gold hover:-translate-y-0.5";

// A prominent "stat" tile used in the hero to surface key information.
const StatCell = ({ value, label, valueClass = "text-white", tip }) => {
  const inner = (
    <div className="w-full rounded-2xl border border-white/10 bg-white/[0.05] px-4 py-3 backdrop-blur-sm transition-colors duration-200 hover:border-white/20">
      <p className={`font-heading font-extrabold text-2xl leading-none ${valueClass}`}>{value}</p>
      <p className="mt-2 text-[0.66rem] font-bold uppercase tracking-wider text-white/50">{label}</p>
    </div>
  );
  return tip ? (
    <Tooltip label={tip} className="flex-1 min-w-[120px]">
      {inner}
    </Tooltip>
  ) : (
    <div className="flex-1 min-w-[120px]">{inner}</div>
  );
};

const RecommendationsSection = ({ gameId, gameName, sourceGame = null, compact = false }) => {
  const { user } = useAuth();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);
  const [selectedGame, setSelectedGame] = useState(null); // For expanded view
  const [showExpanded, setShowExpanded] = useState(false);
  const [wildcardCount, setWildcardCount] = useState(0);
  const previousGameIdsRef = useRef(new Set()); // Use ref to track previous games
  const isLoadingRef = useRef(false); // Track loading state to prevent race conditions

  const handleGameClick = (game) => {
    setSelectedGame(game);
    setShowExpanded(true);
  };

  const handleCloseExpanded = () => {
    setShowExpanded(false);
    setSelectedGame(null);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    const newRefreshCount = refreshCount + 1;
    setRefreshCount(newRefreshCount);
  };

  // Single useEffect to handle both initial load and refresh
  useEffect(() => {
    if (!gameId) return;

    const fetchRecommendations = async (refresh = 0) => {
      if (isLoadingRef.current) {
        return;
      }

      isLoadingRef.current = true;

      try {
        const { games, metadata } = await getSmartRecommendations(gameId, 4, refresh, user?.id || null);

        const backendWildcardCount = metadata.wildcard_count || 0;
        const processedGames = games;

        if (processedGames.length === 0) {
          return;
        }

        const finalGames = [];

        for (const game of processedGames) {
          if (!game || !game.id) {
            continue;
          }

          const isWildcard = Boolean(game.is_wildcard);

          finalGames.push({
            ...game,
            isWildcard: isWildcard,
            name: game.title || game.name || "Untitled Game",
          });
        }

        setRecommendations(() => finalGames);
        setWildcardCount(backendWildcardCount);
      } catch (err) {
        console.error("❌ Error fetching recommendations:", err);
        if (refresh === 0) {
          setRecommendations([]);
          setWildcardCount(0);
        }
      } finally {
        isLoadingRef.current = false;
        if (refresh === 0) {
          setLoading(false);
        } else {
          setRefreshing(false);
        }
      }
    };

    if (refreshCount === 0) {
      setRecommendations([]);
      setWildcardCount(0);
      previousGameIdsRef.current = new Set();
      setLoading(true);
    } else {
      setRecommendations([]);
      setWildcardCount(0);
    }
    fetchRecommendations(refreshCount);

    return () => {
      if (refreshCount === 0) {
        isLoadingRef.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, refreshCount, user?.id]);

  // Reset refresh count when gameId changes
  useEffect(() => {
    setRefreshCount(0);
    setRefreshing(false);
    previousGameIdsRef.current = new Set();
    isLoadingRef.current = false;
  }, [gameId]);

  if (!gameId) return null;

  return (
    <GlassPanel className="p-4 sm:p-6 max-w-[1200px] mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="flex items-center gap-3 font-heading font-extrabold text-white text-xl sm:text-2xl md:text-3xl">
          <span className="h-7 w-1.5 rounded-full bg-gold-gradient shadow-glow-gold" />
          More Games Like This
        </h2>
        <Tooltip label="Show a fresh set of similar games" side="bottom">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={loading || refreshing}
            aria-label="Refresh recommendations"
          >
            {refreshing ? (
              <>
                <span className="h-3.5 w-3.5 rounded-full border-2 border-brand-gold/40 border-t-brand-gold animate-spin" />
                Loading games...
              </>
            ) : (
              "🔄 Refresh"
            )}
          </Button>
        </Tooltip>
      </div>

      {/* Wildcard info */}
      {wildcardCount > 0 && (
        <p className="text-center text-red-400 font-bold text-sm mb-3">
          🎲 {wildcardCount} wildcard game{wildcardCount > 1 ? "s" : ""} included for
          variety!
        </p>
      )}

      {(loading && recommendations.length === 0) || refreshing ? (
        <div className="flex flex-col items-center py-8">
          <span className="h-9 w-9 rounded-full border-4 border-brand-gold/30 border-t-brand-gold animate-spin mb-3" />
          <p className="text-white mb-1">
            {refreshing ? "Loading games..." : "Finding perfect matches..."}
          </p>
          <p className="text-white/60 text-sm">This may take a moment</p>
        </div>
      ) : recommendations.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4 w-full">
          {recommendations.map((game, index) => {
            if (!game || !game.id || !(game.title || game.name)) {
              return null;
            }

            const isWildcard = Boolean(game.is_wildcard);
            // Prefer the backend's explanation (richer: uses AI gameplay profile
            // + your taste when logged in); fall back to a client-side reason.
            const matchInfo = game.match_reason
              ? { reason: game.match_reason, tags: Array.isArray(game.match_tags) ? game.match_tags : [] }
              : getMatchInfo(sourceGame, game, isWildcard);

            return (
              <div
                key={`rec-${game.id}-${refreshCount}-${index}`}
                onClick={() => handleGameClick(game)}
                className={[
                  "group relative cursor-pointer rounded-2xl overflow-hidden transition-all duration-300 ease-out flex flex-col",
                  "hover:-translate-y-2 hover:scale-[1.03]",
                  isWildcard
                    ? "border-[3px] border-red-400 hover:shadow-[0_15px_35px_rgba(255,107,107,0.5)]"
                    : "border-[3px] border-brand-gold hover:border-yellow-300 hover:shadow-[0_15px_35px_rgba(239,191,4,0.4)]",
                ].join(" ")}
              >
                {/* Cover */}
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img
                    src={game.cover_image || game.cover_url || "/assets/images/placeholder.jpg"}
                    alt={game.title || game.name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />

                  {game.rating ? (
                    <div className="absolute top-2 left-2 rounded-lg bg-black/70 px-2 py-1 backdrop-blur-sm">
                      <span className="text-brand-gold font-bold text-[0.7rem]">
                        ★ {game.rating.toFixed(1)}
                      </span>
                    </div>
                  ) : null}

                  {isWildcard && (
                    <div
                      title={`Wildcard: ${game.wildcard_reason || "Surprise pick!"}`}
                      className="absolute top-2 right-2 z-10 h-8 w-8 rounded-full flex items-center justify-center bg-gradient-to-br from-red-400 to-teal-300 border-2 border-white shadow-[0_2px_8px_rgba(255,107,107,0.6)]"
                    >
                      <span className="text-white text-sm">🎲</span>
                    </div>
                  )}

                  <div className="absolute inset-0 flex items-end justify-center bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="mb-2 rounded-lg bg-black/70 px-2 py-1 text-brand-gold font-bold text-[0.7rem]">
                      Click to expand
                    </span>
                  </div>
                </div>

                {/* Info: title, why-recommended, key tags */}
                <div className="flex flex-col gap-1.5 p-2.5 bg-surface-card/70">
                  <p className="text-white font-bold text-xs sm:text-sm leading-tight line-clamp-2">
                    {game.title}
                  </p>

                  <p className="flex items-start gap-1 text-brand-gold text-[11px] leading-snug">
                    <span aria-hidden="true">✨</span>
                    <span className="line-clamp-2">{matchInfo.reason}</span>
                  </p>

                  {matchInfo.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-0.5">
                      {matchInfo.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded-md bg-white/10 border border-white/10 px-1.5 py-0.5 text-[10px] text-white/75"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : !refreshing ? (
        <div className="text-center py-4">
          <p className="text-white text-lg mb-2">No recommendations found</p>
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            Try Again
          </Button>
        </div>
      ) : null}

      {/* Refresh hint */}
      {refreshCount === 0 && recommendations.length > 0 && (
        <div className="mt-3 p-2 rounded-xl bg-brand-gold/10 text-center">
          <p className="text-white text-xs sm:text-sm">
            💡 <strong>Tip:</strong> Click "Refresh" to discover more games with
            gradually broader matching criteria and new wildcards!
          </p>
        </div>
      )}

      {/* Expanded Game View Modal */}
      <Modal
        open={showExpanded}
        onClose={handleCloseExpanded}
        panelClassName="w-full max-w-2xl rounded-3xl border-2 border-brand-gold bg-gradient-to-br from-[#1a1a1a] to-[#2d2d2d] shadow-[0_20px_40px_rgba(0,0,0,0.8)]"
      >
        <div className="flex justify-between items-center p-5 border-b border-brand-gold/30 bg-brand-gold/5">
          <h3 className="font-bold text-xl text-brand-gold">
            {selectedGame?.title || "Game Details"}
          </h3>
          <button
            onClick={handleCloseExpanded}
            className="h-8 w-8 rounded-full flex items-center justify-center text-brand-gold hover:bg-brand-gold/10 transition-colors"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="p-5">
          {selectedGame && (
            <div>
              <GameCard game={selectedGame} showActions={true} compact={false} />

              <div className="mt-6 space-y-4">
                {selectedGame.genres && (
                  <div>
                    <h4 className="text-white font-semibold mb-2">Genres:</h4>
                    <div className="flex flex-wrap gap-2">
                      {safeJsonParse(selectedGame.genres).map((genre, index) => (
                        <span key={genre.id || index} className={chipClass}>
                          {genre.name || genre}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {selectedGame.platforms && (
                  <div>
                    <h4 className="text-white font-semibold mb-2">Platforms:</h4>
                    <div className="flex flex-wrap gap-2">
                      {safeJsonParse(selectedGame.platforms).map((platform, index) => (
                        <span key={platform.id || index} className={chipClass}>
                          {platform.name || platform}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {selectedGame.short_description && (
                  <div>
                    <h4 className="text-brand-gold font-bold mb-2">Description:</h4>
                    <p className="text-white/90 leading-relaxed">
                      {selectedGame.short_description}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </Modal>
    </GlassPanel>
  );
};

const RecommendationsSectionMemo = React.memo(RecommendationsSection);

const GameDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { user, isBlacklisted, addToBlacklist, removeFromBlacklist } = useAuth();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedScreenshotIndex, setSelectedScreenshotIndex] = useState(null);
  const [touchStartX, setTouchStartX] = useState(null);
  const [prosAndCons, setProsAndCons] = useState({ pros: [], cons: [] });
  const [prosAndConsLoading, setProsAndConsLoading] = useState(false);

  // Memoize expensive JSON parsing operations
  const parsedGameData = useMemo(() => {
    if (!game) return null;

    const rawTags = safeJsonParse(game.tags);

    const steamTagsToFilter = [
      "steam achievements", "trading cards", "steam cloud", "achievements",
      "full controller support", "partial controller support", "steam workshop",
      "steam leaderboards", "includes source sdk", "steam trading cards",
      "steam", "controller support", "dlc", "workshop", "cloud saves",
      "vac enabled", "in-app purchases", "includes level editor",
    ];

    const filteredTags = Array.isArray(rawTags)
      ? rawTags.filter((tag) => {
          const tagName = (typeof tag === "object" ? tag.name : tag)?.toLowerCase() || "";
          return !steamTagsToFilter.some((steamTag) => tagName.includes(steamTag));
        })
      : rawTags;

    return {
      genres: safeJsonParse(game.genres),
      platforms: safeJsonParse(game.platforms),
      tags: filteredTags,
      screenshots: safeJsonParse(game.screenshots),
    };
  }, [game]);

  // Inject Aclonica font from Google Fonts
  useEffect(() => {
    const aclonicaLink = document.createElement("link");
    aclonicaLink.href = "https://fonts.googleapis.com/css2?family=Aclonica&display=swap";
    aclonicaLink.rel = "stylesheet";
    document.head.appendChild(aclonicaLink);

    return () => {
      if (document.head.contains(aclonicaLink)) {
        document.head.removeChild(aclonicaLink);
      }
    };
  }, []);

  useEffect(() => {
    const loadGameDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const gameData = await getGameById(id);
        setGame(gameData);

        // Fetch AI pros & cons (pre-generated and stored in DB by the cron job).
        try {
          setProsAndConsLoading(true);
          const prosConsData = await getGameProsAndCons(id);
          if (prosConsData && (prosConsData.pros || prosConsData.cons)) {
            // Normalise entries to strings — the DB may store either bare
            // strings or objects, and the render calls .replace on them.
            setProsAndCons({
              pros: toPointList(prosConsData.pros),
              cons: toPointList(prosConsData.cons),
            });
          } else {
            setProsAndCons({ pros: [], cons: [] });
          }
        } catch (err) {
          console.error("Error loading pros and cons:", err);
          setProsAndCons({ pros: [], cons: [] });
        } finally {
          setProsAndConsLoading(false);
        }
      } catch (err) {
        setError("Failed to load game details. Please try again.");
        console.error("Error loading game:", err);
      } finally {
        setLoading(false);
      }
    };

    loadGameDetails();
  }, [id]);

  const openScreenshot = (index) => setSelectedScreenshotIndex(index);
  const closeScreenshot = () => setSelectedScreenshotIndex(null);
  const shotCount = parsedGameData?.screenshots?.length || 0;
  const showPrevScreenshot = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      setSelectedScreenshotIndex((prev) => (prev > 0 ? prev - 1 : (shotCount || 1) - 1));
    },
    [shotCount],
  );
  const showNextScreenshot = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      setSelectedScreenshotIndex((prev) =>
        prev < (shotCount || 1) - 1 ? prev + 1 : 0,
      );
    },
    [shotCount],
  );

  useEffect(() => {
    if (selectedScreenshotIndex === null) return;
    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft") showPrevScreenshot();
      if (e.key === "ArrowRight") showNextScreenshot();
      if (e.key === "Escape") closeScreenshot();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedScreenshotIndex, showPrevScreenshot, showNextScreenshot]);

  const handleFavorite = async () => {
    if (game) {
      await toggleFavorite(game.id);

      const isNowFavorite = isGameFavorite;
      await recordInteraction(game.id, INTERACTION_TYPES.DISLIKE, {
        type: "card_action",
        source: "game_detail_page",
        previousState: isNowFavorite ? "favorited" : "unfavorited",
      });

      trackGameInteraction(game.id, "favorite");
    }
  };

  const handleShare = () => {
    if (navigator.share && game) {
      navigator.share({
        title: game.title,
        text: `Check out ${game.title}`,
        url: window.location.href,
      });
    }
  };

  const handleBlacklist = async (e) => {
    if (e) e.stopPropagation();
    if (!user || !game) return;

    try {
      const isGameBlacklisted = isBlacklisted(game.id);
      if (isGameBlacklisted) {
        await removeFromBlacklist(game.id);
      } else {
        await addToBlacklist(game.id);
      }
      trackGameInteraction(game.id, "blacklist");
    } catch (err) {
      console.error("Error toggling blacklist:", err);
    }
  };

  const formatReleaseDate = (dateString) => {
    if (!dateString) return "TBA";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return "TBA";
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 py-8 animate-pulse">
        <div className="h-12 w-44 rounded-full bg-white/10 mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-1 h-[400px] rounded-3xl bg-white/10" />
          <div className="md:col-span-2 rounded-3xl bg-white/10 p-6 space-y-4">
            <div className="h-16 bg-white/10 rounded" />
            <div className="h-10 w-2/3 bg-white/10 rounded" />
            <div className="h-72 bg-white/10 rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <div className="rounded-2xl border border-red-400/40 bg-red-500/10 text-white px-5 py-4 mb-6">
          {error}
        </div>
        <Button variant="primary" size="lg" onClick={() => navigate("/")}>
          ← Back to Games
        </Button>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <div className="rounded-2xl border border-brand-gold/40 bg-brand-gold/10 text-white px-5 py-4 mb-6">
          Game not found.
        </div>
        <Button variant="primary" size="lg" onClick={() => navigate("/")}>
          ← Back to Games
        </Button>
      </div>
    );
  }

  const userScore = game.user_score || game.rating || 0;
  const criticScore = game.critic_score || game.metacritic_score || 0;
  const isGameFavorite = isFavorite(game.id);
  const isGameBlacklisted = isBlacklisted(game.id);

  // Data provenance + canonical platforms (client: attribute scores/data and
  // keep platform display consistent everywhere).
  const dataSource = dataSourceOf(game);
  const platformData = getPlatformFamilies(game.platforms);
  const releaseYear = game.release_date
    ? new Date(game.release_date).getFullYear()
    : null;
  const primaryGenre =
    parsedGameData?.genres?.[0]?.genre?.name ||
    parsedGameData?.genres?.[0]?.name ||
    (typeof parsedGameData?.genres?.[0] === "string" ? parsedGameData.genres[0] : null);
  const heroBackdrop =
    screenshotUrl(parsedGameData?.screenshots?.[0]) ||
    game.cover_image ||
    game.cover_url ||
    "/assets/images/placeholder.jpg";

  // "About this game" prefers the ORIGINAL/official description; AI summary is
  // only a clearly-labelled fallback when no official text exists.
  const officialDescription =
    game.long_summary || game.summary || game.short_description || "";
  const aiDescription = game.ai_generated_summary || "";
  const aboutIsOfficial = Boolean(officialDescription);
  const aboutText = officialDescription || aiDescription;

  const scoreTone = (score) =>
    score >= 80
      ? "border-neon-green text-neon-green"
      : score >= 60
        ? "border-brand-gold text-brand-gold"
        : score >= 40
          ? "border-orange-400 text-orange-400"
          : "border-red-400 text-red-400";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 py-6 sm:py-8">
      {/* Back Button */}
      <button
        onClick={() => navigate("/")}
        className="group mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-sm font-semibold text-white/80 transition-all duration-200 hover:border-brand-gold/50 hover:text-brand-gold"
      >
        <span className="transition-transform duration-200 group-hover:-translate-x-0.5">←</span>
        Back to Games
      </button>

      {/* Hero: cinematic banner with floating poster + prominent stat strip */}
      <div className="relative rounded-[28px] border border-white/10 shadow-card mb-6">
        {/* blurred backdrop (clipped) */}
        <div className="absolute inset-0 overflow-hidden rounded-[28px]">
          <img
            src={heroBackdrop}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover scale-110 blur-2xl opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/90 to-surface/60" />
          <div className="absolute inset-0 bg-brand-gradient-135 opacity-[0.12] mix-blend-overlay" />
          <div className="absolute -top-24 left-1/3 h-72 w-72 rounded-full bg-brand-purple/25 blur-3xl" />
        </div>

        <div className="relative p-5 sm:p-8 md:p-10">
          <div className="grid grid-cols-1 md:grid-cols-[minmax(0,270px),1fr] gap-6 md:gap-9 items-start">
            {/* Poster */}
            <div className="relative mx-auto md:mx-0 w-[210px] sm:w-[250px] md:w-full">
              <div className="absolute -inset-1.5 rounded-3xl bg-brand-gradient opacity-40 blur-xl" />
              <img
                src={game.cover_image || game.cover_url || "/assets/images/placeholder.jpg"}
                alt={game.title}
                className="relative w-full aspect-[3/4] object-cover rounded-3xl border border-white/15 shadow-[0_24px_60px_rgba(0,0,0,0.6)]"
              />
            </div>

            {/* Info */}
            <div className="min-w-0">
              {/* provenance / age chips */}
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {dataSource && (
                  <SourceChip title={`Game data imported from ${dataSource}`}>
                    Data · {dataSource}
                  </SourceChip>
                )}
                {game.age_rating && <SourceChip title="Age rating">{game.age_rating}</SourceChip>}
                {primaryGenre && <SourceChip title="Primary genre">{primaryGenre}</SourceChip>}
              </div>

              <h1
                className="font-heading font-extrabold text-white text-3xl sm:text-4xl md:text-5xl leading-[1.05] tracking-tight"
                style={{ textShadow: "0 4px 24px rgba(0,0,0,0.5)" }}
              >
                {game.title}
              </h1>

              {/* Game modes */}
              {game.game_modes && game.game_modes.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {game.game_modes.map((mode, index) => (
                    <span
                      key={mode.id || index}
                      className="rounded-full border border-brand-gold/40 bg-brand-gold/10 px-3.5 py-1 text-sm font-semibold text-brand-gold"
                    >
                      {mode.name}
                    </span>
                  ))}
                  {game.game_modes.some((mode) => mode.name?.toLowerCase().includes("multi")) &&
                    !game.game_modes.some((mode) => mode.name?.toLowerCase().includes("single")) && (
                      <span className="rounded-full border border-brand-gold/40 bg-brand-gold/10 px-3.5 py-1 text-sm font-semibold text-brand-gold">
                        Single Player
                      </span>
                    )}
                </div>
              )}

              {/* Prominent stat strip — key info at a glance */}
              <div className="flex flex-wrap gap-3 mt-6">
                {criticScore > 0 && (
                  <StatCell
                    value={Math.round(criticScore)}
                    label="Critic · Metacritic"
                    valueClass={scoreTone(criticScore).split(" ").find((c) => c.startsWith("text-"))}
                    tip="Aggregated critic score from Metacritic (0–100)."
                  />
                )}
                {userScore > 0 && (
                  <StatCell
                    value={`${userScore.toFixed(1)}`}
                    label={`Users${dataSource ? ` · ${dataSource}` : ""}`}
                    valueClass={scoreTone(userScore * 20).split(" ").find((c) => c.startsWith("text-"))}
                    tip={`Average player rating out of 5${dataSource ? `, from ${dataSource}` : ""}.`}
                  />
                )}
                {releaseYear && (
                  <StatCell value={releaseYear} label="Released" tip={formatReleaseDate(game.release_date)} />
                )}
                {platformData.families.length > 0 && (
                  <StatCell
                    value={platformData.families.length}
                    label={platformData.families.length > 1 ? "Platforms" : "Platform"}
                    tip={platformData.families.map((f) => f.family).join(" · ")}
                  />
                )}
              </div>

              {/* Action bar — every control has a tooltip */}
              <div className="flex flex-wrap items-center gap-2.5 mt-6">
                <Tooltip label={isGameFavorite ? "Remove from favorites" : "Add to favorites"}>
                  <button
                    onClick={handleFavorite}
                    aria-label={isGameFavorite ? "Remove from favorites" : "Add to favorites"}
                    className={[
                      "inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-heading font-bold transition-all duration-200 hover:-translate-y-0.5",
                      isGameFavorite
                        ? "bg-gold-gradient text-[#0b0916] shadow-glow-gold"
                        : "border border-brand-gold/50 text-brand-gold hover:bg-brand-gold/10",
                    ].join(" ")}
                  >
                    <span className="text-lg">{isGameFavorite ? "♥" : "♡"}</span>
                    {isGameFavorite ? "Favorited" : "Favorite"}
                  </button>
                </Tooltip>

                <Tooltip label="Share this game">
                  <button onClick={handleShare} aria-label="Share this game" className={iconActionClass}>
                    <ShareIcon size={18} />
                  </button>
                </Tooltip>

                <Tooltip
                  wide
                  label={
                    user
                      ? isGameBlacklisted
                        ? "Remove from blacklist, allow this game in results again"
                        : "Hide this game from your search results and recommendations"
                      : "Sign in to hide this game"
                  }
                >
                  <button
                    onClick={handleBlacklist}
                    disabled={!user}
                    aria-label="Hide this game"
                    className={[
                      "h-11 w-11 rounded-full flex items-center justify-center text-xl border transition-all duration-200",
                      !user
                        ? "text-white/40 border-white/10 cursor-not-allowed"
                        : isGameBlacklisted
                          ? "text-red-400 border-red-400/40 bg-red-500/10 hover:bg-red-500/20"
                          : "text-white/85 border-white/12 bg-white/[0.05] hover:border-red-400/50 hover:text-red-400 hover:-translate-y-0.5",
                    ].join(" ")}
                  >
                    ⛔
                  </button>
                </Tooltip>

                {game.official_website && (
                  <Tooltip label="Visit the official website">
                    <a
                      href={game.official_website}
                      target="_blank"
                      rel="noreferrer"
                      aria-label="Official website"
                      className={iconActionClass}
                    >
                      🔗
                    </a>
                  </Tooltip>
                )}

                {game.store_page && (
                  <Tooltip label="Open the store page">
                    <a
                      href={game.store_page}
                      target="_blank"
                      rel="noreferrer"
                      aria-label="Store page"
                      className={iconActionClass}
                    >
                      🛒
                    </a>
                  </Tooltip>
                )}

                {game.trailer_url && (
                  <Tooltip label="Watch the trailer">
                    <a
                      href={game.trailer_url}
                      target="_blank"
                      rel="noreferrer"
                      aria-label="Watch trailer"
                      className={iconActionClass}
                    >
                      ▶
                    </a>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Details grid */}
      <div className="grid grid-cols-1 gap-6 mb-6">
        <div>

          {/* Release Info / Game Details — symmetric split panel */}
          <div className="mt-6 flex flex-col sm:flex-row rounded-3xl overflow-hidden border border-white/10 bg-surface-card/40 backdrop-blur-md">
            <div className="flex-1 p-5 sm:p-7">
              <h3 className="flex items-center gap-2 text-white font-bold text-lg mb-4">
                <span className="text-brand-gold">📅</span> Release Info
              </h3>
              <div className="space-y-2.5 text-sm sm:text-base">
                <p className="flex justify-between gap-3">
                  <span className="text-white/55">Release Date</span>
                  <span className="text-white font-medium text-right">{formatReleaseDate(game.release_date)}</span>
                </p>
                {game.age_rating && (
                  <p className="flex justify-between gap-3">
                    <span className="text-white/55">Age Rating</span>
                    <span className="text-white font-medium text-right">{game.age_rating}</span>
                  </p>
                )}
                {game.developer && (
                  <p className="flex justify-between gap-3">
                    <span className="text-white/55">Developer</span>
                    <button
                      type="button"
                      onClick={() => navigate(`/company/developer/${encodeURIComponent(game.developer)}`)}
                      title={`See all games by ${game.developer}`}
                      className="text-white font-medium text-right transition-colors hover:text-brand-gold hover:underline"
                    >
                      {game.developer}
                    </button>
                  </p>
                )}
                {game.publisher && (
                  <p className="flex justify-between gap-3">
                    <span className="text-white/55">Publisher</span>
                    <button
                      type="button"
                      onClick={() => navigate(`/company/publisher/${encodeURIComponent(game.publisher)}`)}
                      title={`See all games from ${game.publisher}`}
                      className="text-white font-medium text-right transition-colors hover:text-brand-gold hover:underline"
                    >
                      {game.publisher}
                    </button>
                  </p>
                )}
              </div>
            </div>

            <div className="w-full sm:w-px h-px sm:h-auto bg-white/10" />

            <div className="flex-1 p-5 sm:p-7">
              <h3 className="flex items-center gap-2 text-white font-bold text-lg mb-4">
                <span className="text-brand-gold">🏷️</span> Game Details
              </h3>

              {parsedGameData?.genres?.length > 0 && (
                <div className="mb-4">
                  <p className="text-white/55 text-sm mb-1.5">Genres</p>
                  <div className="flex flex-wrap gap-1.5">
                    {parsedGameData.genres.map((genreObj, index) => {
                      const genreLabel =
                        genreObj?.genre?.name ||
                        genreObj?.name ||
                        (typeof genreObj === "string" ? genreObj : "Unknown");
                      return (
                        <span key={genreObj?.genre?.id || genreObj?.id || index} className={chipClass}>
                          {String(genreLabel)}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {platformData.families.length > 0 && (
                <div>
                  <p className="text-white/55 text-sm mb-1.5">
                    Platforms
                    {platformData.count > platformData.families.length && (
                      <span className="ml-2 text-xs text-white/35">
                        {platformData.count} editions across {platformData.families.length} platform
                        {platformData.families.length > 1 ? "s" : ""}
                      </span>
                    )}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {platformData.families.map((f) => (
                      <span
                        key={f.family}
                        title={
                          f.members.length > 1
                            ? `${f.members.length} ${f.family} platforms: ${f.members.join(", ")}`
                            : f.members[0] || f.family
                        }
                        className={`${chipClass} ${f.members.length > 1 ? "cursor-help" : ""}`}
                      >
                        {f.family}
                        {f.members.length > 1 && (
                          <span className="ml-1.5 text-brand-gold/70">({f.members.length})</span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Screenshots */}
      {parsedGameData?.screenshots?.length > 0 && (
        <GlassPanel className="mt-6 sm:mt-8 p-5 sm:p-8">
          <h2 className="flex items-center gap-3 font-heading font-extrabold text-white text-2xl sm:text-3xl mb-6">
            <span className="h-7 w-1.5 rounded-full bg-gold-gradient shadow-glow-gold" />
            Screenshots
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 md:gap-4">
            {parsedGameData.screenshots.map((screenshot, index) => (
              <img
                key={index}
                src={screenshotUrl(screenshot)}
                alt={`${game.title} screenshot ${index + 1}`}
                title="Click to enlarge"
                loading="lazy"
                onClick={() => openScreenshot(index)}
                className="w-full h-full object-cover rounded-2xl border border-white/10 cursor-pointer shadow-card transition-all duration-300 hover:border-brand-gold/60 hover:shadow-[0_0_25px_rgba(239,191,4,0.35)] hover:-translate-y-1"
              />
            ))}
          </div>
        </GlassPanel>
      )}

      {/* About this game */}
      {(aboutText ||
        prosAndConsLoading ||
        prosAndCons.pros.length > 0 ||
        prosAndCons.cons.length > 0) && (
        <GlassPanel className="mt-6 sm:mt-8 p-5 sm:p-10">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="flex items-center gap-3 font-heading font-extrabold text-white text-2xl sm:text-3xl">
              <span className="h-7 w-1.5 rounded-full bg-gold-gradient shadow-glow-gold" />
              About this game
            </h2>
            {aboutText &&
              (aboutIsOfficial ? (
                <OfficialBadge
                  source={dataSource}
                  title={`Official description${dataSource ? ` from ${dataSource}` : ""}`}
                />
              ) : (
                <AiBadge title="No official description was available, so this is an AI-generated summary." />
              ))}
          </div>

          {aboutText ? (
            <p className="text-white/85 leading-relaxed text-sm sm:text-base mb-2">
              {aboutText}
            </p>
          ) : (
            <p className="text-white/50 italic text-sm mb-2">
              No description available for this game yet.
            </p>
          )}
          {!aboutIsOfficial && aboutText && (
            <p className="text-white/45 text-xs mb-6">
              Official description unavailable. Showing an AI-generated summary until source data is available.
            </p>
          )}
          {aboutIsOfficial && <div className="mb-6" />}

          {/* Pros & Cons — always AI-generated, labelled transparently */}
          {(prosAndConsLoading ||
            prosAndCons.pros.length > 0 ||
            prosAndCons.cons.length > 0) && (
            <>
              <div className="flex flex-wrap items-center gap-2.5 mb-3">
                <h3 className="font-heading font-bold text-white text-lg">Pros &amp; Cons</h3>
                <AiBadge />
              </div>

              {!prosAndConsLoading &&
              prosAndCons.pros.length === 0 &&
              prosAndCons.cons.length === 0 ? (
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-5 text-center">
                  <p className="text-white/55 text-sm">
                    A concise pros &amp; cons summary isn't available for this game yet.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="rounded-2xl p-4 sm:p-5 border border-neon-green/30 bg-neon-green/5">
                    <h4 className="flex items-center gap-2 text-neon-green font-bold mb-3">
                      <span className="flex items-center justify-center h-5 w-5 rounded-full bg-neon-green/20 text-xs">✓</span>
                      Pros
                    </h4>
                    <div className="space-y-2">
                      {prosAndConsLoading
                        ? Array.from({ length: 4 }, (_, index) => (
                            <div key={index} className="h-4 w-4/5 rounded gg-shimmer" />
                          ))
                        : prosAndCons.pros.map((pro, index) => (
                            <p key={index} className="flex gap-2 text-white/85 text-sm">
                              <span className="text-neon-green mt-0.5">+</span>
                              <span>{pro.replace(/^[-•+\s]+/, "").trim()}</span>
                            </p>
                          ))}
                    </div>
                  </div>

                  <div className="rounded-2xl p-4 sm:p-5 border border-red-500/30 bg-red-500/5">
                    <h4 className="flex items-center gap-2 text-red-400 font-bold mb-3">
                      <span className="flex items-center justify-center h-5 w-5 rounded-full bg-red-500/20 text-xs">!</span>
                      Cons
                    </h4>
                    <div className="space-y-2">
                      {prosAndConsLoading
                        ? Array.from({ length: 4 }, (_, index) => (
                            <div key={index} className="h-4 w-4/5 rounded gg-shimmer" />
                          ))
                        : prosAndCons.cons.map((con, index) => (
                            <p key={index} className="flex gap-2 text-white/85 text-sm">
                              <span className="text-red-400 mt-0.5">−</span>
                              <span>{con.replace(/^[-•\s]+/, "").trim()}</span>
                            </p>
                          ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </GlassPanel>
      )}

      {/* Tags */}
      {game.tags && game.tags.length > 0 && parsedGameData?.tags?.length > 0 && (
        <GlassPanel className="mt-6 sm:mt-8 p-5 sm:p-8">
          <h2 className="flex items-center gap-3 font-heading font-extrabold text-white text-xl sm:text-2xl mb-4">
            <span className="h-6 w-1.5 rounded-full bg-gold-gradient shadow-glow-gold" />
            Tags &amp; Features
          </h2>
          <div className="flex flex-wrap gap-2">
            {parsedGameData.tags.slice(0, 24).map((tag, index) => {
              const label = capitalizeText(typeof tag === "string" ? tag : tag?.name || "");
              if (!label) return null;
              return (
                <button
                  key={tag.id || index}
                  type="button"
                  onClick={() => navigate(`/tags/${encodeURIComponent(label)}`)}
                  title={`Browse ${label} games`}
                  className="rounded-full px-3 py-1 text-sm font-semibold text-white/85 bg-brand-purple/15 border border-brand-purple/30 transition-all duration-150 hover:border-brand-gold hover:text-brand-gold hover:bg-brand-gold/10 hover:-translate-y-0.5"
                >
                  #{label}
                </button>
              );
            })}
          </div>
        </GlassPanel>
      )}

      {/* Screenshot lightbox */}
      <Modal
        open={selectedScreenshotIndex !== null}
        onClose={closeScreenshot}
        panelClassName="w-full max-w-5xl rounded-3xl border border-white/10 bg-gradient-to-br from-[#0f0f0f] to-[#1a1a1a] shadow-[0_32px_64px_rgba(0,0,0,0.8)]"
      >
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <h3 className="text-white font-bold text-lg sm:text-xl">
              {game?.title || "Game"} Screenshots
            </h3>
            <span className="rounded-full px-3 py-1 text-xs sm:text-sm text-white bg-white/10 border border-white/20 font-bold">
              {selectedScreenshotIndex !== null ? selectedScreenshotIndex + 1 : 0} /{" "}
              {shotCount}
            </span>
          </div>
          <button
            onClick={closeScreenshot}
            className="h-9 w-9 rounded-full flex items-center justify-center text-white bg-white/10 border border-white/20 hover:bg-white/20 hover:scale-105 transition-all"
            aria-label="Close"
            title="Close (Esc)"
          >
            ✕
          </button>
        </div>

        <div
          className="relative flex items-center justify-center min-h-[50vh] sm:min-h-[60vh] p-2 sm:p-6"
          onTouchStart={(e) => setTouchStartX(e.touches[0].clientX)}
          onTouchEnd={(e) => {
            if (touchStartX === null) return;
            const deltaX = e.changedTouches[0].clientX - touchStartX;
            if (deltaX > 50) showPrevScreenshot();
            if (deltaX < -50) showNextScreenshot();
            setTouchStartX(null);
          }}
        >
          {selectedScreenshotIndex !== null &&
            (parsedGameData?.screenshots?.[selectedScreenshotIndex] ||
              game?.screenshots?.[selectedScreenshotIndex]) && (
            <>
              <button
                onClick={showPrevScreenshot}
                disabled={selectedScreenshotIndex === 0}
                aria-label="Previous screenshot"
                title="Previous (←)"
                className={[
                  "absolute left-2 sm:left-5 top-1/2 -translate-y-1/2 z-10 h-11 w-11 sm:h-14 sm:w-14 rounded-full flex items-center justify-center text-white bg-white/10 border border-white/20 backdrop-blur-md transition-all duration-300",
                  selectedScreenshotIndex > 0
                    ? "hover:bg-white/20 hover:scale-110"
                    : "opacity-30 cursor-not-allowed",
                ].join(" ")}
              >
                ←
              </button>

              <img
                src={screenshotUrl(
                  parsedGameData?.screenshots?.[selectedScreenshotIndex] ??
                    game.screenshots[selectedScreenshotIndex],
                )}
                alt={`Game screenshot ${selectedScreenshotIndex + 1}`}
                className="max-w-[85%] sm:max-w-[90%] max-h-[55vh] sm:max-h-[65vh] rounded-2xl border-2 border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.5)] object-contain transition-transform duration-300 hover:scale-[1.02]"
              />

              <button
                onClick={showNextScreenshot}
                disabled={selectedScreenshotIndex >= (shotCount || 1) - 1}
                aria-label="Next screenshot"
                title="Next (→)"
                className={[
                  "absolute right-2 sm:right-5 top-1/2 -translate-y-1/2 z-10 h-11 w-11 sm:h-14 sm:w-14 rounded-full flex items-center justify-center text-white bg-white/10 border border-white/20 backdrop-blur-md transition-all duration-300",
                  selectedScreenshotIndex < (shotCount || 1) - 1
                    ? "hover:bg-white/20 hover:scale-110"
                    : "opacity-30 cursor-not-allowed",
                ].join(" ")}
              >
                →
              </button>

              {/* Thumbnails */}
              <div className="absolute inset-x-0 bottom-0 flex justify-center gap-1.5 p-3 sm:p-4 overflow-x-auto bg-gradient-to-t from-black/80 to-transparent [&::-webkit-scrollbar]:hidden">
                {parsedGameData?.screenshots.map((screenshot, index) => (
                  <img
                    key={index}
                    src={screenshotUrl(screenshot)}
                    alt={`Thumbnail ${index + 1}`}
                    onClick={() => setSelectedScreenshotIndex(index)}
                    className={[
                      "w-12 h-8 sm:w-[60px] sm:h-10 rounded-lg object-cover cursor-pointer transition-all duration-300 border-2",
                      index === selectedScreenshotIndex
                        ? "opacity-100 border-white"
                        : "opacity-60 border-transparent hover:opacity-100 hover:scale-110",
                    ].join(" ")}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Games you may like — bottom of page */}
      <div className="mt-8 sm:mt-10 mb-4">
        <RecommendationsSectionMemo gameId={id} gameName={game?.title} sourceGame={game} compact={false} />
      </div>
    </div>
  );
};

export default React.memo(GameDetailPage);
